// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// The comment list's view (Figma November 2026, frame 0b): To decide or All,
// No grouping (high risk first) or the Status sections, and which statuses show.
import { useEffect, useMemo, useRef, useState } from "react";

import { bySeverity, severityRank } from "./pinGroups";
import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  REVIEW_COMMENT_GROUPS,
  type ReviewCommentGroup,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";

export type ReviewListTab = "toDecide" | "all";
export type ReviewListGroupBy = "none" | "status";

export type ReviewListStatusFilter = {
  shown: ReadonlySet<ReviewCommentGroup>;
  onToggle: (status: ReviewCommentGroup, checked: boolean) => void;
  onReset: () => void;
};

export const EVERY_STATUS: ReadonlySet<ReviewCommentGroup> = new Set(
  REVIEW_COMMENT_GROUPS,
);

/** Some statuses are unticked. None ticked narrows nothing, like the type boxes. */
export function statusFilterNarrows(
  shown: ReadonlySet<ReviewCommentGroup>,
): boolean {
  return shown.size > 0 && shown.size < REVIEW_COMMENT_GROUPS.length;
}

export type RiskCounts = { high: number; medium: number; low: number };

function isToDecide(comment: CommentWithOptionalTags): boolean {
  return !comment.isDeleted && reviewCommentGroup(comment) === "unresolved";
}

export function riskCounts(
  comments: readonly Pick<CommentWithOptionalTags, "severity" | "isRequired">[],
): RiskCounts {
  const counts: RiskCounts = { high: 0, medium: 0, low: 0 };
  for (const comment of comments) {
    // why: a person's comment, or a finding from before severities, has no
    // risk to report; counting it as low calls her own note a low-risk finding.
    if (comment.severity == null && !comment.isRequired) continue;
    const rank = severityRank(comment);
    if (rank === 2) counts.high += 1;
    else if (rank === 1) counts.medium += 1;
    else counts.low += 1;
  }
  return counts;
}

export function useReviewListView({
  comments,
  selectedCommentId,
  enabled,
  allComments,
  statusFilter,
}: {
  comments: readonly CommentWithOptionalTags[];
  selectedCommentId: string | null;
  /** Off, the list draws `comments` as given, in its sections. */
  enabled: boolean;
  /** Every comment on the review, before the type and source filters. */
  allComments?: readonly CommentWithOptionalTags[];
  /** The review's Status boxes, which the workspace owns; absent, every status shows. */
  statusFilter?: ReviewListStatusFilter;
}) {
  const [tab, setTab] = useState<ReviewListTab>("toDecide");
  const [groupBy, setGroupBy] = useState<ReviewListGroupBy>("none");
  // why EVERY_STATUS: without the review's Status boxes nothing is unticked.
  const statuses = statusFilter?.shown ?? EVERY_STATUS;
  const narrowed = enabled && statusFilterNarrows(statuses);
  // why: the tab counts read this, so they count only what the filter lets through.
  const byStatus = useMemo(
    () =>
      narrowed
        ? comments.filter((comment) =>
            statuses.has(reviewCommentGroup(comment)),
          )
        : comments,
    [comments, narrowed, statuses],
  );

  const toDecide = useMemo(() => byStatus.filter(isToDecide), [byStatus]);
  const allCount = useMemo(
    () => byStatus.filter((comment) => !comment.isDeleted).length,
    [byStatus],
  );
  // why: read the unfiltered review, not whether a filter is on. The runner's
  // own reviewer picks always narrow, so a filter test would hide this banner from her.
  const hiddenToDecide = useMemo(
    () => (allComments ?? comments).some(isToDecide),
    [allComments, comments],
  );

  // why: a pin picked on the artwork can belong to a comment that already has
  // a status; To decide would leave its card nowhere on screen.
  const selectedOutsideTab =
    enabled &&
    tab === "toDecide" &&
    selectedCommentId != null &&
    comments.some(
      (comment) => comment._id === selectedCommentId && !isToDecide(comment),
    );
  // why: the latch follows the selection, not the tab. A decided pin picked on
  // All never switched tabs, so a switch-only latch would leave it unarmed and
  // undo her next click on To decide. Once armed, only the tab she picked and
  // the card leaving this tab by a judgment may move it.
  const revealedSelection = useRef<string | null>(null);
  const revealedTab = useRef<ReviewListTab>(tab);
  useEffect(() => {
    const tabPicked = revealedTab.current !== tab;
    revealedTab.current = tab;
    if (revealedSelection.current !== selectedCommentId) {
      revealedSelection.current = selectedCommentId;
      if (selectedOutsideTab) setTab("all");
      return;
    }
    if (tabPicked) return;
    if (selectedOutsideTab) setTab("all");
  }, [selectedCommentId, selectedOutsideTab, tab]);

  const shown = useMemo(() => {
    if (!enabled) return comments;
    const base = tab === "toDecide" ? toDecide : byStatus;
    return groupBy === "none" ? bySeverity(base) : base;
  }, [byStatus, comments, enabled, groupBy, tab, toDecide]);

  return {
    tab,
    setTab,
    groupBy,
    setGroupBy,
    counts: { toDecide: toDecide.length, all: allCount },
    shown,
    flat: enabled && groupBy === "none",
    statusFilter,
    /** The status boxes hide something; an empty list then blames the filter. */
    statusNarrowed: narrowed,
    // why: To decide holds only comments with no status, so its Approved and
    // Rejected headings would always read 0; an unticked status shows no heading.
    showsSection: (key: ReviewCommentGroup) =>
      enabled
        ? tab === "toDecide"
          ? key === "unresolved"
          : !narrowed || statuses.has(key)
        : true,
    /** To decide is empty because every comment has a status, not because there are none. */
    allDecided:
      enabled &&
      tab === "toDecide" &&
      allCount > 0 &&
      toDecide.length === 0 &&
      !hiddenToDecide,
  };
}
